import { User, UserPermissions, UserRole } from '../types/user';
import { isCognitoConfigured } from './cognitoConfig';
import { cognitoService } from './cognitoService';
import { cognitoAdminService } from './cognitoAdminService';

// Mock authentication service (fallback when Cognito is not configured)
// In production with Cognito, this is only used as fallback

const STORAGE_KEY = 'scan-store-retrieve-auth';
const USERS_STORAGE_KEY = 'scan-store-retrieve-users';

// Default admin user (for initial setup - only used in mock mode)
const DEFAULT_ADMIN: User = {
  id: 'admin-001',
  username: 'admin',
  email: 'admin@example.com',
  role: 'admin',
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  isActive: true,
};

// Initialize users storage (mock mode only)
const initializeUsers = (): User[] => {
  const stored = localStorage.getItem(USERS_STORAGE_KEY);
  if (stored) {
    return JSON.parse(stored);
  }
  const users = [DEFAULT_ADMIN];
  localStorage.setItem(USERS_STORAGE_KEY, JSON.stringify(users));
  return users;
};

// Simple password storage (mock mode only)
const PASSWORD_STORAGE_KEY = 'scan-store-retrieve-passwords';
const initializePasswords = (): Record<string, string> => {
  const stored = localStorage.getItem(PASSWORD_STORAGE_KEY);
  if (stored) {
    return JSON.parse(stored);
  }
  const passwords: Record<string, string> = {
    'admin-001': 'admin123',
  };
  localStorage.setItem(PASSWORD_STORAGE_KEY, JSON.stringify(passwords));
  return passwords;
};

// Mock authentication methods (used when Cognito is not configured)
const mockAuth = {
  async login(username: string, password: string): Promise<{ user: User; token: string } | null> {
    const users = initializeUsers();
    const passwords = initializePasswords();

    const user = users.find(u => u.username === username && u.isActive);
    if (!user) return null;

    if (passwords[user.id] !== password) return null;

    user.lastLogin = new Date().toISOString();
    user.updatedAt = new Date().toISOString();
    localStorage.setItem(USERS_STORAGE_KEY, JSON.stringify(users));

    const token = btoa(JSON.stringify({ userId: user.id, timestamp: Date.now() }));
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ user, token }));

    return { user, token };
  },

  getCurrentUser(): User | null {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    const { user } = JSON.parse(stored);
    return user;
  },

  logout(): void {
    localStorage.removeItem(STORAGE_KEY);
  },

  isAuthenticated(): boolean {
    return this.getCurrentUser() !== null;
  },

  async addUser(username: string, email: string, password: string, role: UserRole = 'user'): Promise<User> {
    const users = initializeUsers();
    const passwords = initializePasswords();

    if (users.some(u => u.username === username)) {
      throw new Error('Username already exists');
    }

    if (users.some(u => u.email === email)) {
      throw new Error('Email already exists');
    }

    const newUser: User = {
      id: `user-${Date.now()}`,
      username,
      email,
      role,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      isActive: true,
    };

    users.push(newUser);
    passwords[newUser.id] = password;

    localStorage.setItem(USERS_STORAGE_KEY, JSON.stringify(users));
    localStorage.setItem(PASSWORD_STORAGE_KEY, JSON.stringify(passwords));

    return newUser;
  },

  getAllUsers(): User[] {
    return initializeUsers();
  },

  getUserById(userId: string): User | null {
    const users = initializeUsers();
    return users.find(u => u.id === userId) || null;
  },

  async resetPassword(userId: string, newPassword: string): Promise<void> {
    const passwords = initializePasswords();
    passwords[userId] = newPassword;
    localStorage.setItem(PASSWORD_STORAGE_KEY, JSON.stringify(passwords));
  },

  async changePassword(userId: string, oldPassword: string, newPassword: string): Promise<void> {
    const passwords = initializePasswords();
    
    if (passwords[userId] !== oldPassword) {
      throw new Error('Current password is incorrect');
    }

    passwords[userId] = newPassword;
    localStorage.setItem(PASSWORD_STORAGE_KEY, JSON.stringify(passwords));
  },

  async updateUser(userId: string, updates: Partial<User>): Promise<User> {
    const users = initializeUsers();
    const userIndex = users.findIndex(u => u.id === userId);
    
    if (userIndex === -1) {
      throw new Error('User not found');
    }

    users[userIndex] = {
      ...users[userIndex],
      ...updates,
      updatedAt: new Date().toISOString(),
    };

    localStorage.setItem(USERS_STORAGE_KEY, JSON.stringify(users));
    return users[userIndex];
  },

  async deleteUser(userId: string): Promise<void> {
    const users = initializeUsers();
    const passwords = initializePasswords();
    const userIndex = users.findIndex(u => u.id === userId);

    if (userIndex === -1) {
      throw new Error('User not found');
    }

    users.splice(userIndex, 1);
    delete passwords[userId];

    localStorage.setItem(USERS_STORAGE_KEY, JSON.stringify(users));
    localStorage.setItem(PASSWORD_STORAGE_KEY, JSON.stringify(passwords));
  },
};

// Unified authentication service (uses Cognito if configured, otherwise mock)
export const authService = {
  /**
   * Login user
   */
  async login(username: string, password: string): Promise<{ user: User; token: string } | null> {
    if (isCognitoConfigured()) {
      try {
        return await cognitoService.login(username, password);
      } catch (error: any) {
        // If Cognito fails, throw error (don't fallback to mock)
        throw error;
      }
    } else {
      return await mockAuth.login(username, password);
    }
  },

  /**
   * Get current user
   */
  async getCurrentUser(): Promise<User | null> {
    if (isCognitoConfigured()) {
      try {
        return await cognitoService.getCurrentUser();
      } catch {
        return null;
      }
    } else {
      return mockAuth.getCurrentUser();
    }
  },

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    if (isCognitoConfigured()) {
      try {
        await cognitoService.logout();
      } catch (error) {
        console.error('Cognito logout error:', error);
        // Clear local storage as fallback
        localStorage.removeItem(STORAGE_KEY);
      }
    } else {
      mockAuth.logout();
    }
  },

  /**
   * Check if user is authenticated
   */
  async isAuthenticated(): Promise<boolean> {
    if (isCognitoConfigured()) {
      return await cognitoService.isAuthenticated();
    } else {
      return mockAuth.isAuthenticated();
    }
  },

  /**
   * Get user permissions
   */
  getUserPermissions(user: User | null): UserPermissions {
    if (!user) {
      return {
        canAddUsers: false,
        canResetPasswords: false,
        canChangePassword: false,
        canViewDocuments: false,
        canUploadDocuments: false,
        canEditDocuments: false,
        canDeleteDocuments: false,
        canDownloadDocuments: false,
        canShareDocuments: false,
      };
    }

    const isAdmin = user.role === 'admin';
    return {
      canAddUsers: isAdmin,
      canResetPasswords: isAdmin,
      canChangePassword: true,
      canViewDocuments: true,
      canUploadDocuments: true,
      canEditDocuments: true,
      canDeleteDocuments: true,
      canDownloadDocuments: true,
      canShareDocuments: true,
    };
  },

  /**
   * Add new user (admin only)
   * Note: In Cognito mode, this uses Lambda function via API Gateway
   */
  async addUser(username: string, email: string, password: string, role: UserRole = 'user'): Promise<User> {
    if (isCognitoConfigured()) {
      // Use Cognito Admin API via Lambda
      try {
        return await cognitoAdminService.createUser(username, email, password, role);
      } catch (error: any) {
        throw new Error(error.message || 'Failed to create user');
      }
    } else {
      return await mockAuth.addUser(username, email, password, role);
    }
  },

  /**
   * Get all users (admin only)
   * Note: In Cognito mode, this uses Lambda function via API Gateway
   */
  async getAllUsers(): Promise<User[]> {
    if (isCognitoConfigured()) {
      // Use Cognito Admin API via Lambda
      try {
        return await cognitoAdminService.getAllUsers();
      } catch (error: any) {
        throw new Error(error.message || 'Failed to list users');
      }
    } else {
      return mockAuth.getAllUsers();
    }
  },

  /**
   * Get user by ID
   */
  async getUserById(userId: string): Promise<User | null> {
    if (isCognitoConfigured()) {
      // In Cognito mode, get current user or use Admin API
      const currentUser = await this.getCurrentUser();
      if (currentUser && currentUser.id === userId) {
        return currentUser;
      }
      // For other users, would need Admin API
      return null;
    } else {
      return mockAuth.getUserById(userId);
    }
  },

  /**
   * Reset user password (admin only)
   * Note: In Cognito mode, this uses Lambda function via API Gateway
   */
  async resetPassword(userId: string, newPassword: string): Promise<void> {
    if (isCognitoConfigured()) {
      // Use Cognito Admin API via Lambda
      try {
        return await cognitoAdminService.resetPassword(userId, newPassword);
      } catch (error: any) {
        throw new Error(error.message || 'Failed to reset password');
      }
    } else {
      return await mockAuth.resetPassword(userId, newPassword);
    }
  },

  /**
   * Delete user (admin only)
   */
  async deleteUser(userId: string): Promise<void> {
    if (isCognitoConfigured()) {
      try {
        return await cognitoAdminService.deleteUser(userId);
      } catch (error: any) {
        throw new Error(error.message || 'Failed to delete user');
      }
    } else {
      return await mockAuth.deleteUser(userId);
    }
  },

  /**
   * Change password (current user)
   */
  async changePassword(userId: string, oldPassword: string, newPassword: string): Promise<void> {
    if (isCognitoConfigured()) {
      // Use Cognito's changePassword API
      const { updatePassword } = await import('aws-amplify/auth');
      try {
        await updatePassword({
          oldPassword,
          newPassword,
        });
      } catch (error: any) {
        throw new Error(error.message || 'Failed to change password');
      }
    } else {
      return await mockAuth.changePassword(userId, oldPassword, newPassword);
    }
  },

  /**
   * Update user
   */
  async updateUser(userId: string, updates: Partial<User>): Promise<User> {
    if (isCognitoConfigured()) {
      const { cognitoAdminService } = await import('./cognitoAdminService');
      return await cognitoAdminService.updateUser(userId, updates);
    } else {
      return await mockAuth.updateUser(userId, updates);
    }
  },
};
